import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowRight,
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle,
  Info,
  AlertCircle,
  Loader2,
  X,
  Users,
  Calendar,
  LayoutGrid,
  ClipboardCheck,
} from 'lucide-react';
import { api } from '../services/api';
import {
  AGE_GROUPS,
  generateSeasonOptions,
  getTeamFormatInfo,
  TeamFormation,
  TeamGender,
  SurfaceType,
  TrainingSession,
} from '../types';
import {
  getFormationsByAgeGroup,
} from '../data/formations';

// ─── Constants ────────────────────────────────────────────────────────────────

const GENDERS: TeamGender[] = ['Male', 'Female', 'Mixed'];
const SURFACE_TYPES: SurfaceType[] = ['Grass', 'Astro Turf', 'Hard Surface', 'Indoor'];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const FORMATION_OPTIONS = [
  '1-4-4-2', '1-4-3-3', '1-3-5-2', '1-4-2-3-1',
  '1-3-4-3', '1-4-1-4-1', '1-5-3-2', '1-5-4-1', 'Custom',
];

const STEPS = [
  { label: 'Team', icon: Users },
  { label: 'Training', icon: Calendar },
  { label: 'Formations', icon: LayoutGrid },
  { label: 'Review', icon: ClipboardCheck },
];

// ─── Wizard State ─────────────────────────────────────────────────────────────

interface WizardState {
  name: string;
  ageGroup: string;
  season: string;
  gender: TeamGender | '';
  assessmentInterval: number;
  trainingSessions: TrainingSession[];
  formationMode: 'simple' | 'advanced';
  primaryInPossession?: TeamFormation;
  primaryOutOfPossession?: TeamFormation;
  secondaryInPossession?: TeamFormation;
  secondaryOutOfPossession?: TeamFormation;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateTeamWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);

  const [state, setState] = useState<WizardState>({
    name: '',
    ageGroup: '',
    season: '',
    gender: '',
    assessmentInterval: 6,
    trainingSessions: [],
    formationMode: 'simple',
  });

  const [newSession, setNewSession] = useState({
    dayOfWeek: '',
    location: '',
    startTime: '',
    endTime: '',
    surfaceType: '' as SurfaceType | '',
  });

  const seasonOptions = useMemo(() => generateSeasonOptions(), []);

  const formatInfo = useMemo(() => {
    if (state.season && state.ageGroup) {
      return getTeamFormatInfo(state.ageGroup, state.season);
    }
    return '';
  }, [state.season, state.ageGroup]);

  const availableFormations = useMemo(() => {
    if (state.ageGroup) {
      return getFormationsByAgeGroup(state.ageGroup);
    }
    return [];
  }, [state.ageGroup]);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const canProceedStep1 = state.name.trim().length >= 2 && state.ageGroup && state.gender && state.assessmentInterval > 0;

  const goNext = () => {
    setError('');
    if (step === 0 && !canProceedStep1) {
      setError('Please fill in all required fields');
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => {
    setError('');
    if (step === 0) {
      navigate('/teams');
    } else {
      setStep((s) => s - 1);
    }
  };

  const goSkip = () => {
    setError('');
    setStep((s) => Math.min(s + 1, 3));
  };

  // ─── Training Sessions ───────────────────────────────────────────────────

  const addSession = () => {
    if (!newSession.dayOfWeek || !newSession.startTime || !newSession.endTime || !newSession.surfaceType) {
      setError('Please fill in day, start time, end time, and surface type');
      return;
    }
    setError('');
    const session: TrainingSession = {
      id: `session-${Date.now()}`,
      dayOfWeek: newSession.dayOfWeek,
      location: newSession.location,
      startTime: newSession.startTime,
      endTime: newSession.endTime,
      surfaceType: newSession.surfaceType as SurfaceType,
    };
    setState((s) => ({ ...s, trainingSessions: [...s.trainingSessions, session] }));
    setNewSession({ dayOfWeek: '', location: '', startTime: '', endTime: '', surfaceType: '' });
    setShowAddSession(false);
  };

  const removeSession = (id: string) => {
    setState((s) => ({
      ...s,
      trainingSessions: s.trainingSessions.filter((t) => t.id !== id),
    }));
  };

  // ─── Formations ───────────────────────────────────────────────────────────

  const handleFormationSelect = (
    type: 'primaryInPossession' | 'primaryOutOfPossession' | 'secondaryInPossession' | 'secondaryOutOfPossession',
    notation: string
  ) => {
    if (!notation) {
      setState((s) => ({ ...s, [type]: undefined }));
      return;
    }
    if (notation === 'Custom') {
      setState((s) => ({
        ...s,
        [type]: { formationId: `custom-${Date.now()}`, notation: 'Custom', name: 'Custom Formation', isCustom: true },
      }));
      return;
    }
    const found = availableFormations.find((f) => f.notation === notation);
    const formation: TeamFormation = {
      formationId: found?.id || `preset-${notation}`,
      notation,
      name: found?.name || notation,
      isCustom: false,
    };
    setState((s) => ({ ...s, [type]: formation }));
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: state.name.trim(),
        ageGroup: state.ageGroup,
        season: state.season || seasonOptions[0],
        gender: state.gender || undefined,
        assessmentInterval: state.assessmentInterval,
        trainingSessions: state.trainingSessions.length > 0 ? state.trainingSessions : undefined,
        primaryInPossessionFormation: state.primaryInPossession,
        primaryOutOfPossessionFormation: state.primaryOutOfPossession,
        secondaryInPossessionFormation: state.secondaryInPossession,
        secondaryOutOfPossessionFormation: state.secondaryOutOfPossession,
      };
      await api.post('/teams', payload);
      navigate('/teams');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr.response?.data?.error?.message || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step Progress ────────────────────────────────────────────────────────

  const StepProgress = () => (
    <div className="flex items-center w-full mb-8">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.label}>
          <button
            type="button"
            onClick={() => {
              if (i < step) setStep(i);
            }}
            disabled={i > step}
            className={`flex-1 text-center pb-3 text-sm font-medium border-b-2 transition-colors ${
              i === step
                ? 'text-green-700 border-green-600'
                : i < step
                ? 'text-green-600 border-green-600 cursor-pointer'
                : 'text-gray-400 border-gray-200'
            }`}
          >
            {s.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  // ─── Step Header ──────────────────────────────────────────────────────────

  const StepHeader = ({
    icon: Icon,
    title,
    subtitle,
  }: {
    icon: React.ElementType;
    title: string;
    subtitle: string;
  }) => (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-green-700" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    </div>
  );

  // ─── Step 1: Team ─────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-5">
      <StepHeader
        icon={Users}
        title="Team Information"
        subtitle="Tell us about your team"
      />

      <div>
        <Label htmlFor="teamName" className="text-sm font-medium text-gray-700">
          Team Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="teamName"
          value={state.name}
          onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
          placeholder="Enter team name"
          className="mt-1.5"
        />
      </div>

      <div>
        <Label htmlFor="ageGroup" className="text-sm font-medium text-gray-700">
          Age Group <span className="text-red-500">*</span>
        </Label>
        <select
          id="ageGroup"
          value={state.ageGroup}
          onChange={(e) => setState((s) => ({ ...s, ageGroup: e.target.value }))}
          className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Select age group</option>
          {AGE_GROUPS.map((ag) => (
            <option key={ag} value={ag}>{ag}</option>
          ))}
        </select>
        {formatInfo && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>{formatInfo} (from 2026/27 season)</span>
          </div>
        )}
      </div>

      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          Gender <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-3 gap-3">
          {GENDERS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setState((s) => ({ ...s, gender: g }))}
              className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
                state.gender === g
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="season" className="text-sm font-medium text-gray-700">Season</Label>
        <select
          id="season"
          value={state.season}
          onChange={(e) => setState((s) => ({ ...s, season: e.target.value }))}
          className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          {seasonOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div>
        <Label htmlFor="assessmentInterval" className="text-sm font-medium text-gray-700">
          Assessment Frequency <span className="text-red-500">*</span>
        </Label>
        <p className="text-xs text-gray-500 mt-0.5 mb-1.5">How often players should be assessed — this drives your development cycle</p>
        <select
          id="assessmentInterval"
          value={state.assessmentInterval}
          onChange={(e) => setState((s) => ({ ...s, assessmentInterval: Number(e.target.value) }))}
          className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value={0}>Select frequency</option>
          {[2, 3, 4, 5, 6, 7, 8, 10, 12].map((w) => (
            <option key={w} value={w}>Every {w} weeks{w === 6 ? ' (recommended)' : ''}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // ─── Step 2: Training ─────────────────────────────────────────────────────

  const renderStep2 = () => (
    <div className="space-y-5">
      <StepHeader
        icon={Calendar}
        title="Training Schedule"
        subtitle="Add your regular training sessions"
      />

      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Adding training sessions helps you plan your coaching week. You can always add or change these later.
        </p>
      </div>

      {state.trainingSessions.map((session) => (
        <div key={session.id} className="flex items-start justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="space-y-1">
            <p className="font-medium text-gray-900 text-sm">{session.dayOfWeek}</p>
            <p className="text-sm text-gray-600">{session.startTime} – {session.endTime}</p>
            {session.location && <p className="text-sm text-gray-500">{session.location}</p>}
            <span className="inline-block text-xs bg-white text-gray-600 px-2 py-0.5 rounded border border-gray-200">
              {session.surfaceType}
            </span>
          </div>
          <button
            type="button"
            onClick={() => removeSession(session.id)}
            className="text-red-400 hover:text-red-600 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Remove session"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {showAddSession ? (
        <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-white">
          <div>
            <Label className="text-sm font-medium text-gray-700">Day of Week <span className="text-red-500">*</span></Label>
            <select
              value={newSession.dayOfWeek}
              onChange={(e) => setNewSession((s) => ({ ...s, dayOfWeek: e.target.value }))}
              className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select day...</option>
              {DAYS_OF_WEEK.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">Location</Label>
            <Input
              value={newSession.location}
              onChange={(e) => setNewSession((s) => ({ ...s, location: e.target.value }))}
              placeholder="e.g., Riverside Playing Fields"
              className="mt-1.5"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Start Time <span className="text-red-500">*</span></Label>
              <Input
                type="time"
                value={newSession.startTime}
                onChange={(e) => setNewSession((s) => ({ ...s, startTime: e.target.value }))}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">End Time <span className="text-red-500">*</span></Label>
              <Input
                type="time"
                value={newSession.endTime}
                onChange={(e) => setNewSession((s) => ({ ...s, endTime: e.target.value }))}
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">
              Surface Type <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {SURFACE_TYPES.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setNewSession((s) => ({ ...s, surfaceType: st }))}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
                    newSession.surfaceType === st
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" onClick={addSession} size="sm" className="bg-green-600 hover:bg-green-700">
              Add Session
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddSession(false);
                setNewSession({ dayOfWeek: '', location: '', startTime: '', endTime: '', surfaceType: '' });
                setError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddSession(true)}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          <Plus className="w-4 h-4" />
          Add Training Session
        </button>
      )}
    </div>
  );

  // ─── Formation Select Helper ──────────────────────────────────────────────

  const FormationSelect = ({
    label,
    type,
    value,
  }: {
    label: string;
    type: 'primaryInPossession' | 'primaryOutOfPossession' | 'secondaryInPossession' | 'secondaryOutOfPossession';
    value?: TeamFormation;
  }) => {
    const formationNotations = useMemo(() => {
      const notations = new Set<string>();
      availableFormations.forEach((f) => notations.add(f.notation));
      FORMATION_OPTIONS.forEach((n) => { if (n !== 'Custom') notations.add(n); });
      return Array.from(notations);
    }, []);

    return (
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</Label>
        <select
          value={value?.notation || ''}
          onChange={(e) => handleFormationSelect(type, e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Select a formation</option>
          {formationNotations.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
          <option value="Custom">+ Custom Formation</option>
        </select>
        {value && (
          <p className="text-xs text-gray-500 mt-1">
            Selected: {value.notation} {value.isCustom ? '(Custom)' : ''}
          </p>
        )}
      </div>
    );
  };

  // ─── Step 3: Formations ───────────────────────────────────────────────────

  const renderStep3 = () => (
    <div className="space-y-5">
      <StepHeader
        icon={LayoutGrid}
        title="Team Formations"
        subtitle="Choose your preferred formations"
      />

      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">Formation Mode</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setState((s) => ({
              ...s,
              formationMode: 'simple',
              primaryOutOfPossession: undefined,
              secondaryInPossession: undefined,
              secondaryOutOfPossession: undefined,
            }))}
            className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
              state.formationMode === 'simple'
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
            }`}
          >
            Simple
          </button>
          <button
            type="button"
            onClick={() => setState((s) => ({ ...s, formationMode: 'advanced' }))}
            className={`py-2.5 px-4 rounded-lg border text-sm font-medium transition-all min-h-[44px] ${
              state.formationMode === 'advanced'
                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
            }`}
          >
            Advanced
          </button>
        </div>
      </div>

      {state.formationMode === 'simple' ? (
        <>
          <FormationSelect
            label="Primary Formation"
            type="primaryInPossession"
            value={state.primaryInPossession}
          />
          <p className="text-sm text-gray-500">
            Select one formation your team will use most often. Switch to advanced mode for separate attacking and defending formations.
          </p>
        </>
      ) : (
        <div className="space-y-4">
          <FormationSelect label="Primary In-Possession" type="primaryInPossession" value={state.primaryInPossession} />
          <FormationSelect label="Primary Out-of-Possession" type="primaryOutOfPossession" value={state.primaryOutOfPossession} />
          <FormationSelect label="Secondary In-Possession (Optional)" type="secondaryInPossession" value={state.secondaryInPossession} />
          <FormationSelect label="Secondary Out-of-Possession (Optional)" type="secondaryOutOfPossession" value={state.secondaryOutOfPossession} />
        </div>
      )}
    </div>
  );

  // ─── Step 4: Review ───────────────────────────────────────────────────────

  const renderStep4 = () => (
    <div className="space-y-5">
      <StepHeader
        icon={ClipboardCheck}
        title="Review & Create"
        subtitle="Check everything looks good"
      />

      {/* Team Details */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Team Details</h3>
          <button
            type="button"
            onClick={() => setStep(0)}
            className="text-xs text-green-600 hover:text-green-700 font-medium"
          >
            Edit
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Name</span>
            <span className="font-medium text-gray-900">{state.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Age Group</span>
            <span className="font-medium text-gray-900">{state.ageGroup}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Gender</span>
            <span className="font-medium text-gray-900">{state.gender}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Season</span>
            <span className="font-medium text-gray-900">{state.season || seasonOptions[0]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Assessment Frequency</span>
            <span className="font-medium text-gray-900">Every {state.assessmentInterval} weeks</span>
          </div>
        </div>
      </div>

      {/* Training Sessions */}
      {state.trainingSessions.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Training Sessions</h3>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-xs text-green-600 hover:text-green-700 font-medium"
            >
              Edit
            </button>
          </div>
          <div className="space-y-2">
            {state.trainingSessions.map((session) => (
              <div key={session.id} className="flex justify-between items-center text-sm">
                <div>
                  <span className="font-medium text-gray-900">{session.dayOfWeek}</span>
                  <span className="text-gray-500 ml-2">{session.startTime} – {session.endTime}</span>
                </div>
                <span className="text-xs text-gray-400">{session.surfaceType}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formations */}
      {(state.primaryInPossession || state.primaryOutOfPossession) && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Formations</h3>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="text-xs text-green-600 hover:text-green-700 font-medium"
            >
              Edit
            </button>
          </div>
          <div className="space-y-2 text-sm">
            {state.primaryInPossession && (
              <div className="flex justify-between">
                <span className="text-gray-500">{state.formationMode === 'simple' ? 'Primary' : 'In-Possession'}</span>
                <span className="font-medium text-gray-900">{state.primaryInPossession.notation}</span>
              </div>
            )}
            {state.primaryOutOfPossession && (
              <div className="flex justify-between">
                <span className="text-gray-500">Out-of-Possession</span>
                <span className="font-medium text-gray-900">{state.primaryOutOfPossession.notation}</span>
              </div>
            )}
            {state.secondaryInPossession && (
              <div className="flex justify-between">
                <span className="text-gray-500">Secondary In</span>
                <span className="font-medium text-gray-900">{state.secondaryInPossession.notation}</span>
              </div>
            )}
            {state.secondaryOutOfPossession && (
              <div className="flex justify-between">
                <span className="text-gray-500">Secondary Out</span>
                <span className="font-medium text-gray-900">{state.secondaryOutOfPossession.notation}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ─── Main Render ──────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto px-4 py-6 max-w-xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Your Team</h1>
          <p className="text-sm text-gray-500 mt-1">Let's set up your team details</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/teams')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Step Progress */}
      <StepProgress />

      {/* Error */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Card */}
      <Card className="shadow-sm border border-gray-200">
        <CardContent className="p-6">
          {step === 0 && renderStep1()}
          {step === 1 && renderStep2()}
          {step === 2 && renderStep3()}
          {step === 3 && renderStep4()}

          {/* Navigation inside card */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={goBack}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors min-h-[44px] px-2"
            >
              {step === 0 ? 'Cancel' : (
                <span className="flex items-center gap-1">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </span>
              )}
            </button>

            <div className="flex items-center gap-3">
              {(step === 1 || step === 2) && (
                <button
                  type="button"
                  onClick={goSkip}
                  className="text-sm text-gray-400 hover:text-gray-600 font-medium transition-colors min-h-[44px] px-2"
                >
                  Skip
                </button>
              )}

              {step < 3 ? (
                <Button
                  type="button"
                  onClick={goNext}
                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Create Team
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
